import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Chip,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  ZoomableGroup,
} from 'react-simple-maps';
import { geoCentroid } from 'd3-geo';
import PropTypes from 'prop-types';
import Iconify from '../common/iconify.component';
import { getPlatformLabel } from '../../config/platforms.config';

const GEO_URL =
  'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';
const DEFAULT_CENTER = [0, 20];
const DEFAULT_ZOOM = 1;
const getCountryZoom = (countryId) => {
  const largeCountries = new Set([
    'US',
    'USA',
    'CA',
    'CAN',
    'BR',
    'BRA',
    'AU',
    'AUS',
  ]);
  return largeCountries.has(String(countryId || '').toUpperCase()) ? 3 : 5;
};
const COUNTRY_NAME_ALIASES = {
  'united states': ['united states of america', 'usa', 'us'],
  'united states of america': ['united states', 'usa', 'us'],
  'south korea': ['korea, republic of', 'republic of korea'],
  'north korea': [
    "korea (democratic people's republic of)",
    "democratic people's republic of korea",
  ],
  russia: ['russian federation'],
  vietnam: ['viet nam'],
  syria: ['syrian arab republic'],
  iran: ['iran, islamic republic of'],
  tanzania: ['united republic of tanzania'],
  bolivia: ['bolivia (plurinational state of)'],
  venezuela: ['venezuela (bolivarian republic of)'],
  laos: ["lao people's democratic republic"],
  moldova: ['moldova, republic of'],
  palestine: ['palestine, state of'],
  brunei: ['brunei darussalam'],
  macedonia: ['north macedonia'],
};

const formatNumber = (value) =>
  new Intl.NumberFormat('en', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(Number(value) || 0);

const getPlatformDisplayLabel = (platformLabel) =>
  getPlatformLabel(platformLabel) || platformLabel || 'Unknown';

const filteredUserCount = (platforms = [], activePlatformSet = null) => {
  if (!platforms.length) return 0;

  return platforms
    .filter(
      (platform) => !activePlatformSet || activePlatformSet.has(platform.label)
    )
    .reduce((sum, platform) => sum + (Number(platform.value) || 0), 0);
};

const getCount = (
  item,
  activePlatformSet = null,
  hasPlatformFilter = false
) => {
  if (!hasPlatformFilter) {
    return Number(item.uniqueDevices ?? item.value) || 0;
  }

  return filteredUserCount(item.platforms, activePlatformSet);
};

const getDotRadius = (count, min, max, minRadius, maxRadius) => {
  if (max === min) return (minRadius + maxRadius) / 2;
  return minRadius + ((count - min) / (max - min)) * (maxRadius - minRadius);
};

const getLegendSeries = (minCount, maxCount) => {
  if (maxCount <= 0) {
    return [0.25, 0.5, 0.75, 1].map((scale) => ({
      scale,
      value: 0,
    }));
  }

  if (minCount === maxCount) {
    return [0.25, 0.5, 0.75, 1].map((scale) => ({
      scale,
      value: Math.round(maxCount),
    }));
  }

  return [0, 1 / 3, 2 / 3, 1].map((progress, index) => ({
    scale: 0.25 + index * 0.25,
    value: Math.round(minCount + progress * (maxCount - minCount)),
  }));
};

const HeatmapMapView = ({
  locationsData,
  breakdownMode,
  selectedCountry,
  selectedCountryFocusCoordinates,
  highlightedDotId,
  activePlatforms,
  onCountrySelect,
  onReset,
}) => {
  const viewportRef = useRef({
    center: DEFAULT_CENTER,
    zoom: DEFAULT_ZOOM,
  });
  const animationFrameRef = useRef(null);
  const isPointerInteractingRef = useRef(false);
  const pendingSelectionRef = useRef(null);
  const countryCentroidsRef = useRef({});
  const lastFocusedCityRef = useRef(null);
  const [hoveredDotKey, setHoveredDotKey] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [viewport, setViewport] = useState({
    center: DEFAULT_CENTER,
    zoom: DEFAULT_ZOOM,
  });
  const [visibleCountryId, setVisibleCountryId] = useState(null);
  const hasPlatformFilter = activePlatforms.length > 0;
  const activePlatformSet = useMemo(
    () => (hasPlatformFilter ? new Set(activePlatforms) : null),
    [activePlatforms, hasPlatformFilter]
  );
  const countryLookup = useMemo(() => {
    const lookup = {};

    (locationsData?.countries || []).forEach((country) => {
      const normalizedName = String(country.name || country.country || '')
        .trim()
        .toLowerCase();

      lookup[
        String(country.id || '')
          .trim()
          .toUpperCase()
      ] = country;
      lookup[normalizedName] = country;

      (COUNTRY_NAME_ALIASES[normalizedName] || []).forEach((alias) => {
        lookup[
          String(alias || '')
            .trim()
            .toLowerCase()
        ] = country;
      });
    });

    return lookup;
  }, [locationsData]);

  const countryDots = useMemo(() => {
    const counts = (locationsData?.countries || []).map((country) =>
      getCount(country, activePlatformSet, hasPlatformFilter)
    );
    const min = counts.length ? Math.min(...counts) : 0;
    const max = counts.length ? Math.max(...counts) : 1;
    const zoomScale = 1 / Math.sqrt(viewport.zoom);

    return (locationsData?.countries || [])
      .filter(
        (country) => !selectedCountry || country.id !== selectedCountry.id
      )
      .map((country) => {
        const count = getCount(country, activePlatformSet, hasPlatformFilter);

        return {
          ...country,
          key: country.id,
          label: country.name,
          count,
          radius: getDotRadius(count, min, max, 3, 12) * zoomScale,
          isCity: false,
        };
      })
      .filter((dot) => dot.count > 0);
  }, [
    activePlatformSet,
    hasPlatformFilter,
    locationsData,
    selectedCountry,
    viewport.zoom,
  ]);

  const countryDotsLookup = useMemo(
    () =>
      Object.fromEntries(
        countryDots.map((countryDot) => [countryDot.id, countryDot])
      ),
    [countryDots]
  );

  const visibleSelectedCountry = useMemo(() => {
    if (!selectedCountry || visibleCountryId !== selectedCountry.id)
      return null;

    return selectedCountry;
  }, [selectedCountry, visibleCountryId]);

  const cityDots = useMemo(() => {
    if (!visibleSelectedCountry) return [];

    const counts = visibleSelectedCountry.cities.map((city) =>
      getCount(city, activePlatformSet, hasPlatformFilter)
    );
    const min = counts.length ? Math.min(...counts) : 0;
    const max = counts.length ? Math.max(...counts) : 1;
    const zoomScale = 1 / Math.sqrt(viewport.zoom);

    return visibleSelectedCountry.cities
      .map((city) => {
        const count = getCount(city, activePlatformSet, hasPlatformFilter);
        return {
          ...city,
          key: city.id,
          label: city.name,
          count,
          radius: getDotRadius(count, min, max, 1.5, 6) * zoomScale,
          isCity: true,
        };
      })
      .filter((dot) => dot.count > 0 && Array.isArray(dot.coordinates));
  }, [
    activePlatformSet,
    hasPlatformFilter,
    viewport.zoom,
    visibleSelectedCountry,
  ]);

  const dots = useMemo(
    () => [...countryDots, ...cityDots],
    [cityDots, countryDots]
  );

  const hoveredDotData = useMemo(
    () => dots.find((dot) => dot.key === hoveredDotKey) || null,
    [dots, hoveredDotKey]
  );

  const totalUsers = useMemo(() => {
    if (selectedCountry) {
      return getCount(selectedCountry, activePlatformSet, hasPlatformFilter);
    }

    return (locationsData?.countries || []).reduce(
      (sum, country) =>
        sum + getCount(country, activePlatformSet, hasPlatformFilter),
      0
    );
  }, [activePlatformSet, hasPlatformFilter, locationsData, selectedCountry]);

  const legendMinMax = useMemo(() => {
    const source = selectedCountry ? cityDots : countryDots;
    const counts = source.map((item) => item.count);

    return {
      minCount: counts.length ? Math.min(...counts) : 0,
      maxCount: counts.length ? Math.max(...counts) : 1,
    };
  }, [cityDots, countryDots, selectedCountry]);

  const tooltipPlatforms = hoveredDotData?.platforms || [];
  const sqrtZoom = Math.sqrt(viewport.zoom);
  const showPulseAnimation = cityDots.length <= 150;
  const mapSurface = alpha('#F8FBFF', 0.88);
  const panelBorder = alpha('#A9B8C8', 0.55);
  const softShadow = '0 18px 38px rgba(15, 23, 42, 0.10)';
  const controlsBg = alpha('#FFFFFF', 0.9);

  const handleDotMouseEnter = useCallback((dotKey, event) => {
    setHoveredDotKey(dotKey);
    setTooltipPos({
      x: event.clientX,
      y: event.clientY,
    });
  }, []);

  const handleDotMouseLeave = useCallback(() => {
    setHoveredDotKey(null);
  }, []);

  const cancelViewportAnimation = () => {
    if (animationFrameRef.current && typeof window !== 'undefined') {
      window.cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  };

  const applyViewport = (nextViewport) => {
    viewportRef.current = nextViewport;
    setViewport(nextViewport);
  };

  const animateViewportTo = (
    nextCenter,
    nextZoom,
    duration = 420,
    onComplete = null
  ) => {
    if (!Array.isArray(nextCenter) || nextCenter.length !== 2) return;

    cancelViewportAnimation();

    const nextViewport = {
      center: nextCenter,
      zoom: nextZoom,
    };

    if (typeof window === 'undefined' || isPointerInteractingRef.current) {
      applyViewport(nextViewport);
      if (onComplete) onComplete();
      return;
    }

    const startViewport = viewportRef.current;
    const startTime = window.performance.now();
    const easeOutCubic = (progress) => 1 - (1 - progress) ** 3;

    const tick = (currentTime) => {
      if (isPointerInteractingRef.current) {
        applyViewport(nextViewport);
        if (onComplete) onComplete();
        animationFrameRef.current = null;
        return;
      }

      const progress = Math.min((currentTime - startTime) / duration, 1);
      const easedProgress = easeOutCubic(progress);
      const interpolatedViewport = {
        center: [
          startViewport.center[0] +
            (nextCenter[0] - startViewport.center[0]) * easedProgress,
          startViewport.center[1] +
            (nextCenter[1] - startViewport.center[1]) * easedProgress,
        ],
        zoom:
          startViewport.zoom + (nextZoom - startViewport.zoom) * easedProgress,
      };

      applyViewport(interpolatedViewport);

      if (progress < 1) {
        animationFrameRef.current = window.requestAnimationFrame(tick);
        return;
      }

      if (onComplete) onComplete();
      animationFrameRef.current = null;
    };

    animationFrameRef.current = window.requestAnimationFrame(tick);
  };

  const focusViewport = (nextCenter, nextZoom) => {
    animateViewportTo(nextCenter, nextZoom);
  };

  const handleResetView = () => {
    setHoveredDotKey(null);
    pendingSelectionRef.current = null;
    setVisibleCountryId(null);
    animateViewportTo(DEFAULT_CENTER, DEFAULT_ZOOM, 480);
    onReset();
  };

  const handleMoveStart = () => {
    isPointerInteractingRef.current = true;
    cancelViewportAnimation();
  };

  const handleMoveEnd = (position) => {
    isPointerInteractingRef.current = false;
    viewportRef.current = {
      center: position.coordinates,
      zoom: position.zoom,
    };

    setViewport(viewportRef.current);
  };

  useEffect(() => {
    if (selectedCountry) {
      const targetCountryId = selectedCountry.id;
      pendingSelectionRef.current = targetCountryId;
      lastFocusedCityRef.current = null;
      setHoveredDotKey(null);
      setVisibleCountryId(null);

      animateViewportTo(
        selectedCountryFocusCoordinates ||
          countryCentroidsRef.current[selectedCountry.id] ||
          selectedCountry.coordinates,
        getCountryZoom(selectedCountry.id),
        420,
        () => {
          if (pendingSelectionRef.current === targetCountryId) {
            setVisibleCountryId(targetCountryId);
          }
        }
      );
      return;
    }

    pendingSelectionRef.current = null;
    lastFocusedCityRef.current = null;
    setVisibleCountryId(null);

    const nextViewport = {
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
    };
    viewportRef.current = nextViewport;
    setViewport(nextViewport);
  }, [selectedCountry, selectedCountryFocusCoordinates]);

  useEffect(() => {
    if (!highlightedDotId || !selectedCountry?.cities?.length) return;
    if (!visibleSelectedCountry) return;
    if (lastFocusedCityRef.current === highlightedDotId) return;

    const highlightedCity =
      selectedCountry.cities.find((city) => city.id === highlightedDotId) ||
      null;

    if (!highlightedCity?.coordinates) return;

    lastFocusedCityRef.current = highlightedDotId;
    focusViewport(highlightedCity.coordinates, Math.max(viewport.zoom, 6));
  }, [highlightedDotId, selectedCountry, visibleSelectedCountry]);

  useEffect(
    () => () => {
      cancelViewportAnimation();
    },
    []
  );

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: { xs: 640, md: 640 },
        bgcolor: '#dfeefb',
        background:
          'radial-gradient(circle at top left, rgba(255,255,255,0.68) 0%, rgba(229,241,251,0.92) 34%, rgba(221,235,247,1) 100%)',
        borderRadius: '28px',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(180deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0) 18%, rgba(11,121,200,0.03) 100%)',
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          position: 'absolute',
          top: 18,
          left: 18,
          right: 18,
          zIndex: 4,
          gap: 2,
        }}
      >
        <Stack
          direction="row"
          spacing={1.2}
          alignItems="center"
          sx={{
            px: 1.5,
            py: 1.25,
            borderRadius: '18px',
            bgcolor: mapSurface,
            border: `1px solid ${panelBorder}`,
            boxShadow: softShadow,
            backdropFilter: 'blur(16px)',
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha('#0B79C8', 0.12),
              color: '#0B79C8',
            }}
          >
            <Iconify
              icon={
                selectedCountry
                  ? 'solar:map-point-wave-linear'
                  : 'solar:global-linear'
              }
              width={18}
            />
          </Box>

          <Box>
            <Typography
              variant="overline"
              sx={{ color: '#6E7B8D', fontSize: 10.5, lineHeight: 1 }}
            >
              {selectedCountry ? 'Focused Region' : 'Map Scope'}
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: '#1F262D',
                fontWeight: 800,
                letterSpacing: '-0.02em',
                lineHeight: 1.1,
                mt: 0.25,
              }}
            >
              {selectedCountry ? selectedCountry.name : 'Global'}
            </Typography>
          </Box>

          {selectedCountry && (
            <Chip
              label="Back to Global"
              size="small"
              onClick={handleResetView}
              sx={{
                ml: 0.5,
                color: '#0B79C8',
                fontWeight: 700,
                bgcolor: alpha('#0B79C8', 0.08),
                border: `1px solid ${alpha('#0B79C8', 0.12)}`,
                cursor: 'pointer',
              }}
            />
          )}
        </Stack>

        <Stack
          spacing={0.6}
          sx={{
            px: 1.5,
            py: 1.2,
            minWidth: 180,
            borderRadius: '18px',
            bgcolor: mapSurface,
            border: `1px solid ${panelBorder}`,
            boxShadow: softShadow,
            backdropFilter: 'blur(16px)',
            alignItems: 'flex-end',
          }}
        >
          <Typography
            variant="overline"
            sx={{ color: '#6E7B8D', fontSize: 10.5, lineHeight: 1 }}
          >
            Total Visits
          </Typography>
          <Typography
            variant="h3"
            sx={{ color: '#0B79C8', fontWeight: 800, lineHeight: 1 }}
          >
            {formatNumber(totalUsers)}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: '#6E7B8D', fontSize: 11, lineHeight: 1 }}
          >
            {selectedCountry
              ? `Visible city activity by ${breakdownMode}`
              : `Visible country activity by ${breakdownMode}`}
          </Typography>
        </Stack>
      </Stack>

      <ComposableMap
        projection="geoMercator"
        projectionConfig={{ scale: 140, center: DEFAULT_CENTER }}
        className="heatmap-map-svg"
        style={{
          width: '100%',
          height: '100%',
          transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <ZoomableGroup
          center={viewport.center}
          zoom={viewport.zoom}
          minZoom={1}
          maxZoom={12}
          className="heatmap-zoomable-group"
          onMoveStart={handleMoveStart}
          onMoveEnd={handleMoveEnd}
          translateExtent={[
            [-Infinity, -Infinity],
            [Infinity, Infinity],
          ]}
          filterZoomEvent={() => true}
        >
          <Geographies geography={GEO_URL}>
            {({ geographies }) => {
              const countryMarkerNodes = [];

              const geographyNodes = geographies.map((geo) => {
                const iso2 = String(
                  geo?.properties?.ISO_A2 || geo?.properties?.iso_a2 || ''
                )
                  .trim()
                  .toUpperCase();
                const iso3 = String(
                  geo?.properties?.ISO_A3 || geo?.properties?.iso_a3 || ''
                )
                  .trim()
                  .toUpperCase();
                const geoName = String(
                  geo?.properties?.NAME || geo?.properties?.name || ''
                )
                  .trim()
                  .toLowerCase();
                const matchedCountry =
                  countryLookup[iso2] ||
                  countryLookup[iso3] ||
                  countryLookup[geoName] ||
                  null;

                if (matchedCountry) {
                  const countryDot = countryDotsLookup[matchedCountry.id];

                  if (countryDot) {
                    const markerCoordinates = geoCentroid(geo);
                    countryCentroidsRef.current[matchedCountry.id] =
                      markerCoordinates;

                    countryMarkerNodes.push(
                      <Marker
                        key={countryDot.key}
                        coordinates={markerCoordinates}
                        className="heatmap-marker"
                        onClick={() =>
                          onCountrySelect({
                            ...countryDot,
                            coordinates: markerCoordinates,
                            focusCoordinates: markerCoordinates,
                          })
                        }
                        onMouseEnter={(event) =>
                          handleDotMouseEnter(countryDot.key, event)
                        }
                        onMouseLeave={handleDotMouseLeave}
                        style={{
                          default: { cursor: 'pointer' },
                          hover: { cursor: 'pointer' },
                          pressed: { cursor: 'pointer' },
                        }}
                      >
                        <circle
                          r={countryDot.radius + 2 / sqrtZoom}
                          fill="none"
                          stroke={alpha('#1E88C8', 0.45)}
                          strokeWidth={0.5 / sqrtZoom}
                          opacity={0.2}
                          style={{ transition: 'all 300ms ease' }}
                        />
                        <circle
                          r={countryDot.radius}
                          fill={alpha('#1E88C8', 0.16)}
                          opacity={0.55}
                          style={{ transition: 'all 300ms ease' }}
                        />
                        <circle
                          r={countryDot.radius}
                          fill="#1E88C8"
                          opacity={0.85}
                          stroke="#1E88C8"
                          strokeWidth={1 / Math.sqrt(viewport.zoom)}
                          style={{ transition: 'all 300ms ease' }}
                        />
                      </Marker>
                    );
                  }
                }

                const isSelected = Boolean(
                  selectedCountry &&
                    matchedCountry &&
                    matchedCountry.id === selectedCountry.id
                );

                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    className="heatmap-geography"
                    fill={isSelected ? alpha('#2065d11a', 0.24) : '#CDD3DD'}
                    stroke={isSelected ? '#2065D1' : '#AEB7C3'}
                    strokeWidth={
                      isSelected ? 2 / viewport.zoom : 0.45 / viewport.zoom
                    }
                    style={{
                      default: {
                        outline: 'none',
                        transition:
                          'fill 0.3s ease, stroke 0.3s ease, stroke-width 0.3s ease',
                      },
                      hover: {
                        outline: 'none',
                        fill: isSelected ? alpha('#2065d11a', 0.28) : '#D7DCE4',
                        transition:
                          'fill 0.3s ease, stroke 0.3s ease, stroke-width 0.3s ease',
                      },
                      pressed: { outline: 'none' },
                    }}
                  />
                );
              });

              return (
                <>
                  {geographyNodes}
                  {countryMarkerNodes}
                </>
              );
            }}
          </Geographies>

          {cityDots.map((dot) => {
            const isHighlighted = highlightedDotId === dot.key;

            return (
              <Marker
                key={dot.key}
                coordinates={dot.coordinates}
                className="heatmap-marker"
                onMouseEnter={(event) => handleDotMouseEnter(dot.key, event)}
                onMouseLeave={handleDotMouseLeave}
                style={{
                  default: { cursor: 'default' },
                  hover: { cursor: 'default' },
                  pressed: { cursor: 'default' },
                }}
              >
                <circle
                  r={dot.radius + 2 / sqrtZoom}
                  fill="none"
                  stroke={isHighlighted ? '#2065D1' : alpha('#1E88C8', 0.45)}
                  strokeWidth={(isHighlighted ? 1.5 : 0.5) / sqrtZoom}
                  opacity={isHighlighted ? 0.8 : 0.2}
                  style={{ transition: 'all 300ms ease' }}
                />
                <circle
                  r={dot.radius}
                  fill={
                    isHighlighted
                      ? alpha('#2065D1', 0.22)
                      : alpha('#1E88C8', 0.16)
                  }
                  opacity={showPulseAnimation || isHighlighted ? 1 : 0.55}
                  style={{ transition: 'all 300ms ease' }}
                >
                  {(showPulseAnimation || isHighlighted) && (
                    <>
                      <animate
                        attributeName="r"
                        from={dot.radius}
                        to={dot.radius + 4 / sqrtZoom}
                        dur={isHighlighted ? '1.2s' : '2s'}
                        repeatCount="indefinite"
                      />
                      <animate
                        attributeName="opacity"
                        from="0.4"
                        to="0"
                        dur={isHighlighted ? '1.2s' : '2s'}
                        repeatCount="indefinite"
                      />
                    </>
                  )}
                </circle>
                <circle
                  r={isHighlighted ? dot.radius * 1.4 : dot.radius}
                  fill={isHighlighted ? '#2065D1' : '#1E88C8'}
                  opacity={isHighlighted ? 1 : 0.85}
                  stroke={isHighlighted ? '#2065D1' : '#1E88C8'}
                  strokeWidth={1 / sqrtZoom}
                  style={{ transition: 'all 300ms ease' }}
                />
              </Marker>
            );
          })}
        </ZoomableGroup>
      </ComposableMap>

      <Stack
        direction="row"
        spacing={1}
        sx={{
          position: 'absolute',
          left: '50%',
          bottom: 18,
          transform: 'translateX(-50%)',
          zIndex: 4,
        }}
      >
        <Tooltip title="Zoom in">
          <span>
            <IconButton
              onClick={() =>
                animateViewportTo(
                  viewportRef.current.center,
                  Math.min(viewportRef.current.zoom * 1.5, 12),
                  320
                )
              }
              disabled={viewport.zoom >= 12}
              sx={{
                width: 44,
                height: 44,
                bgcolor: controlsBg,
                border: `1px solid ${panelBorder}`,
                boxShadow: softShadow,
                backdropFilter: 'blur(16px)',
              }}
            >
              <Iconify icon="ic:round-plus" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Zoom out">
          <span>
            <IconButton
              onClick={() =>
                animateViewportTo(
                  viewportRef.current.center,
                  Math.max(viewportRef.current.zoom / 1.5, 1),
                  320
                )
              }
              disabled={viewport.zoom <= 1}
              sx={{
                width: 44,
                height: 44,
                bgcolor: controlsBg,
                border: `1px solid ${panelBorder}`,
                boxShadow: softShadow,
                backdropFilter: 'blur(16px)',
              }}
            >
              <Iconify icon="ic:round-remove" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Reset view">
          <span>
            <IconButton
              onClick={handleResetView}
              sx={{
                width: 44,
                height: 44,
                bgcolor: controlsBg,
                border: `1px solid ${panelBorder}`,
                boxShadow: softShadow,
                backdropFilter: 'blur(16px)',
              }}
            >
              <Iconify icon="solar:refresh-linear" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <Box
        sx={{
          position: 'absolute',
          bottom: 18,
          left: 18,
          zIndex: 4,
          px: 2.1,
          py: 1.75,
          borderRadius: '20px',
          bgcolor: mapSurface,
          border: `1px solid ${panelBorder}`,
          boxShadow: softShadow,
          backdropFilter: 'blur(16px)',
        }}
      >
        <Typography
          variant="overline"
          sx={{ color: '#5D6A7A', fontSize: 10.5, mb: 0.9, display: 'block' }}
        >
          Legend
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: '#1F262D', mb: 1.4, fontWeight: 600 }}
        >
          {selectedCountry ? 'City' : 'Country'} user distribution
        </Typography>
        <Stack direction="row" spacing={2.2} alignItems="flex-end">
          {getLegendSeries(legendMinMax.minCount, legendMinMax.maxCount).map(
            (item, index) => (
              <Stack
                key={`${item.value}-${index}`}
                spacing={0.8}
                alignItems="center"
              >
                <Box
                  sx={{
                    width: 6 + item.scale * 18,
                    height: 6 + item.scale * 18,
                    borderRadius: '50%',
                    background:
                      'linear-gradient(180deg, rgba(30,136,200,0.78) 0%, rgba(11,121,200,1) 100%)',
                    boxShadow: `0 8px 18px ${alpha('#0B79C8', 0.18)}`,
                    opacity: 0.5 + item.scale * 0.4,
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{ color: '#5D6A7A', fontSize: 11 }}
                >
                  {formatNumber(item.value)}
                </Typography>
              </Stack>
            )
          )}
        </Stack>
      </Box>

      <Box
        sx={{
          position: 'absolute',
          right: 18,
          bottom: 18,
          zIndex: 4,
          px: 1.8,
          py: 1.35,
          maxWidth: 260,
          borderRadius: '18px',
          bgcolor: mapSurface,
          border: `1px solid ${panelBorder}`,
          boxShadow: softShadow,
          backdropFilter: 'blur(16px)',
        }}
      >
        <Stack direction="row" spacing={1.1} alignItems="flex-start">
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha('#0B79C8', 0.1),
              color: '#0B79C8',
              flexShrink: 0,
            }}
          >
            <Iconify icon="solar:cursor-linear" width={16} />
          </Box>
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ color: '#1F262D', fontWeight: 700, mb: 0.3 }}
            >
              {selectedCountry ? 'Map Detail' : 'How To Explore'}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: '#5D6A7A', fontSize: 12, lineHeight: 1.45 }}
            >
              {selectedCountry
                ? 'Country borders are approximate. Hover dots to inspect city-level activity.'
                : 'Click any country dot to zoom in and explore its cities.'}
            </Typography>
          </Box>
        </Stack>
      </Box>

      {hoveredDotData && (
        <Box
          sx={{
            position: 'fixed',
            left: tooltipPos.x,
            top: tooltipPos.y,
            transform: 'translate(-50%, -120%)',
            zIndex: 1500,
            pointerEvents: 'none',
          }}
        >
          <Box
            sx={{
              minWidth: 220,
              px: 2,
              py: 1.6,
              borderRadius: '16px',
              bgcolor: alpha('#F7F7F7', 0.98),
              border: `1px solid ${alpha('#C1C8D0', 0.95)}`,
              boxShadow: '0 18px 40px rgba(31,38,45,0.18)',
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              sx={{ mb: 1.1 }}
            >
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, color: '#1F262D' }}
              >
                {hoveredDotData.label || hoveredDotData.name}
              </Typography>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, color: '#0B79C8' }}
              >
                {formatNumber(
                  getCount(hoveredDotData, activePlatformSet, hasPlatformFilter)
                )}
              </Typography>
            </Stack>

            <Divider />

            <Box sx={{ height: 1, bgcolor: alpha('#B8C0C9', 0.5), mb: 1.1 }} />

            <Stack spacing={0.55}>
              {tooltipPlatforms
                .filter(
                  (platform) =>
                    !activePlatformSet || activePlatformSet.has(platform.label)
                )
                .sort((a, b) => b.value - a.value)
                .map((platform) => (
                  <Stack
                    key={`${hoveredDotData.key || hoveredDotData.id}-${
                      platform.label
                    }`}
                    direction="row"
                    justifyContent="space-between"
                    spacing={2}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: '#667485', fontSize: 12 }}
                    >
                      {breakdownMode === 'platform'
                        ? getPlatformDisplayLabel(platform.label)
                        : platform.label}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: '#1F262D', fontSize: 12, fontWeight: 600 }}
                    >
                      {formatNumber(platform.value)}
                    </Typography>
                  </Stack>
                ))}
            </Stack>
          </Box>
        </Box>
      )}
    </Box>
  );
};

HeatmapMapView.propTypes = {
  locationsData: PropTypes.object.isRequired,
  breakdownMode: PropTypes.oneOf(['platform', 'language']).isRequired,
  selectedCountry: PropTypes.object,
  selectedCountryFocusCoordinates: PropTypes.arrayOf(PropTypes.number),
  highlightedDotId: PropTypes.string,
  activePlatforms: PropTypes.arrayOf(PropTypes.string).isRequired,
  onCountrySelect: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
};

HeatmapMapView.defaultProps = {
  selectedCountry: null,
  selectedCountryFocusCoordinates: null,
  highlightedDotId: null,
};

export default HeatmapMapView;
